/**
 * System: the console's machinery screens behind one sidebar entry (owner,
 * 2026-10-11). Alerts, Billing, DocuSeal, Fax, Mail, Operations, Payments, Settings
 * and Webhooks each keep their own URL (Notifications moved to Emails ›
 * Transactional, 2026-10-16) and detail pages; their headers read
 * "System" and carry these tabs at the top right, in alphabetical order, the
 * way Support carries its views. Below 640px a tab shows its icon only.
 */
import { useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  CreditCard,
  FileSignature,
  HandCoins,
  Mail,
  Printer,
  Settings as SettingsIcon,
  Siren,
  Webhook,
} from "lucide-react";
import { useNavigate } from "react-router";

import { PillTabs } from "@nt/ui/pill-tabs";
import { prefetchNav } from "@/lib/nav-prefetch";

/** Alphabetical, left to right. The first is where the sidebar entry lands. */
export const SYSTEM_SECTIONS = [
  { value: "alerts", label: "Alerts", to: "/alerts", icon: Siren },
  { value: "billing", label: "Billing", to: "/billing", icon: CreditCard },
  {
    value: "docuseal",
    label: "DocuSeal",
    to: "/docuseal",
    icon: FileSignature,
  },
  { value: "fax", label: "Fax", to: "/fax", icon: Printer },
  { value: "mail", label: "Mail", to: "/mail", icon: Mail },
  {
    value: "operations",
    label: "Operations",
    to: "/operations",
    icon: Activity,
  },
  { value: "payments", label: "Payments", to: "/payments", icon: HandCoins },
  // Settings opens on its first tab, AI; every page under /settings is its.
  {
    value: "settings",
    label: "Settings",
    to: "/settings/ai",
    prefix: "/settings",
    icon: SettingsIcon,
  },
  { value: "webhooks", label: "Webhooks", to: "/webhooks", icon: Webhook },
] as const;

export type SystemSection = (typeof SYSTEM_SECTIONS)[number]["value"];

/** True on any System screen or one of its detail pages. */
export function isSystemPath(pathname: string): boolean {
  return SYSTEM_SECTIONS.some((section) => {
    const base = "prefix" in section ? section.prefix : section.to;
    return pathname === base || pathname.startsWith(`${base}/`);
  });
}

export function SystemTabs({ current }: { current: SystemSection }) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const toOf = (value: string) =>
    SYSTEM_SECTIONS.find((section) => section.value === value)?.to;

  return (
    <PillTabs
      aria-label="System section"
      testIdPrefix="system-tab"
      items={SYSTEM_SECTIONS}
      value={current}
      onChange={(next) => {
        const to = toOf(next);
        if (to && next !== current) navigate(to);
      }}
      onIntent={(next) => {
        const to = toOf(next);
        if (to) prefetchNav(client, to);
      }}
    />
  );
}
